import {useState, useCallback} from 'react';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {exerciseFormSchema, ExerciseFormValues} from './ExerciseForm.type';
import {ExerciseProps} from '../../../types/Exercise.type';
import {saveExercise, redirectToEditPage} from './ExerciseForm.service';
import {
  EnhancedExerciseProps,
  formatExerciseForSubmission,
} from './ExerciseForm.map';
import {showUploadInProgressAlert} from '../../../components/media/MediaComponents.service';

export const useExerciseForm = (initialExercise: EnhancedExerciseProps) => {
  const [exerciseValues, setExerciseValues] =
    useState<EnhancedExerciseProps>(initialExercise);
  const [loading, setLoading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [recordEnabled, setRecordEnabled] = useState<boolean>(
    initialExercise.recordEnabled || false,
  );
  const [exerciseTypeSelected, setExerciseTypeSelected] = useState<string>(
    initialExercise.exerciseType || '',
  );
  const [isMediaUploading, setIsMediaUploading] = useState<boolean>(false);

  const form = useForm<ExerciseFormValues>({
    resolver: zodResolver(exerciseFormSchema),
    defaultValues: {
      name: initialExercise.name || '',
      description: initialExercise.description || '',
      recordEnabled: initialExercise.recordEnabled || false,
      exerciseType: initialExercise.exerciseType || '',
    },
  });

  const listExerciseType = [
    {key: 'carga', label: 'Carga'},
    {key: 'tempo', label: 'Tempo'},
    {key: 'repeticao', label: 'Repetição'},
    {key: 'distancia', label: 'Distância'},
    {key: 'calorias', label: 'Calorias'},
  ];

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newExerciseValues = {
      ...exerciseValues,
      [event.target.name]: event.target.value,
    };
    setExerciseValues(newExerciseValues);
  };

  const handleMediaUpdate = (mediaType: string, file: any) => {
    const newExerciseValues = {...exerciseValues, [mediaType]: file};
    setExerciseValues(newExerciseValues);
  };

  const handleMediaUploadStatusChange = useCallback(
    (isUploading: boolean) => {
      setIsMediaUploading(isUploading);
    },
    [],
  );

  const updateExerciseType = (value: string) => {
    setExerciseTypeSelected(value);
  };

  const onSubmit = async (data: ExerciseFormValues) => {
    if (isMediaUploading) {
      showUploadInProgressAlert(['mídia']);
      return;
    }

    if (recordEnabled && !exerciseTypeSelected) {
      form.setError('exerciseType', {
        type: 'manual',
        message: 'Ao habilitar para recordes, você precisa escolher um tipo',
      });
      return;
    }

    const exerciseMutationRaw = formatExerciseForSubmission(
      exerciseValues,
      data,
      recordEnabled,
      exerciseTypeSelected,
    );
    const exerciseMutationValues = Object.fromEntries(
      Object.entries(exerciseMutationRaw).filter(
        ([_, value]) => value !== null && value !== undefined && value !== '',
      ),
    );
    try {
      setLoading(true);
      const response = await saveExercise(
        exerciseMutationValues as ExerciseProps,
        setLoading,
        setProgress,
      );

      const enhancedResponse = response.exercise as EnhancedExerciseProps;
      setExerciseValues(enhancedResponse);

      if (enhancedResponse.id) {
        redirectToEditPage(enhancedResponse.id);
      }
    } catch (error) {
      console.error('Failed to save exercise:', error);
    }
  };

  return {
    form,
    exerciseValues,
    loading,
    progress,
    recordEnabled,
    exerciseTypeSelected,
    listExerciseType,
    isMediaUploading,
    handleChange,
    handleMediaUpdate,
    handleMediaUploadStatusChange,
    updateExerciseType,
    setRecordEnabled,
    onSubmit,
  };
};
